import { api } from "../api/client";
import type { ResidentItem } from "../hooks/useResidents";
import type { Todo } from "../hooks/useTodos";
import { hkToday } from "../lib/dates";
import { downloadCsv, toCsv } from "./csv";

interface IcpItem {
  residentRef: string;
  name: string;
  bed: string | null;
  homeId: string;
  homeName: string | null;
  nextIcpDate: string | null;
  icpFillStatus: string | null;
  addedToPaperForm: boolean;
  slot?: { half: string; month: string };
  label?: string;
  scope?: string;
  why?: string;
}

interface Home {
  id: string;
  name: string;
}

const entityDate = hkToday();
const filename = (entity: string) => `otdesk-${entity}-${entityDate}.csv`;

function qs(params: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** CSV export — residents (default list = active + new, excluding discharged; all homes). */
export async function exportResidentsCsv(): Promise<void> {
  const res = await api<{ items: ResidentItem[] }>("/api/residents");
  const headers = ["name", "bed", "home", "status", "admissionDate", "lastAxDate", "lastIcpDate", "icpFrequency", "nextIcpDate", "icpFillStatus", "addedToPaperForm", "sheetUrl"];
  const rows = res.items.map((r) => ({
    name: r.name,
    bed: r.bed ?? "",
    home: r.homeName,
    status: r.status,
    admissionDate: r.admissionDate ?? "",
    lastAxDate: r.lastAxDate ?? "",
    lastIcpDate: r.lastIcpDate ?? "",
    icpFrequency: r.icpFrequency ?? "",
    nextIcpDate: r.nextIcpDate ?? "",
    icpFillStatus: r.icpFillStatus ?? "",
    addedToPaperForm: r.addedToPaperForm ? "yes" : "no",
    sheetUrl: r.sheetUrl ?? "",
  }));
  downloadCsv(filename("residents"), toCsv(headers, rows));
}

/** CSV export — ICP/reminders across all ranges, all homes. */
export async function exportIcpCsv(): Promise<void> {
  const ranges = ["due", "upcoming", "paper"] as const;
  const results = await Promise.all(
    ranges.map(async (range) => {
      const res = await api<{ items: IcpItem[] }>(`/api/reminders/icp?range=${range}`);
      return { range, items: res.items };
    }),
  );
  const headers = ["range", "name", "bed", "home", "nextIcpDate", "icpFillStatus", "addedToPaperForm", "label", "why"];
  const rows: Record<string, unknown>[] = [];
  for (const { range, items } of results) {
    for (const it of items) {
      rows.push({
        range,
        name: it.name,
        bed: it.bed ?? "",
        home: it.homeName ?? "",
        nextIcpDate: it.nextIcpDate ? it.nextIcpDate.slice(0, 10) : "",
        icpFillStatus: it.icpFillStatus ?? "",
        addedToPaperForm: it.addedToPaperForm ? "yes" : "no",
        label: it.label ?? "",
        why: it.why ?? "",
      });
    }
  }
  downloadCsv(filename("icp"), toCsv(headers, rows));
}

/** CSV export — to-dos (all statuses), all homes. */
export async function exportTodosCsv(): Promise<void> {
  const [todoRes, homes] = await Promise.all([
    api<{ items: Todo[] }>(`/api/todos${qs({ pageSize: "100" })}`),
    api<Home[]>("/api/homes"),
  ]);
  const homeName = new Map(homes.map((h) => [h.id, h.name]));
  const headers = ["title", "status", "home", "dueDate", "completedAt", "source", "tags", "notes"];
  const rows = todoRes.items.map((t) => ({
    title: t.title,
    status: t.status,
    home: t.homeId ? (homeName.get(t.homeId) ?? "") : "",
    dueDate: t.dueDate ? t.dueDate.slice(0, 10) : "",
    completedAt: t.completedAt ? t.completedAt.slice(0, 10) : "",
    source: t.source,
    tags: t.tags.join(";"),
    notes: t.notes ?? "",
  }));
  downloadCsv(filename("todos"), toCsv(headers, rows));
}
